import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { sq } from '@/i18n/sq';
import { Button } from './Button';
import { Modal } from './Modal';

export interface ConfirmOptions {
  title?: string | undefined;
  okLabel?: string | undefined;
  cancelLabel?: string | undefined;
  danger?: boolean | undefined;
}

export type ConfirmFn = (message: string, options?: ConfirmOptions) => Promise<boolean>;

export interface ConfirmDialogProps extends ConfirmOptions {
  open: boolean;
  message: string;
  onResult: (confirmed: boolean) => void;
}

/** Replacement for the prototype's window.confirm(): same question text, Po / Jo buttons. */
export function ConfirmDialog({
  open,
  message,
  title,
  okLabel,
  cancelLabel,
  danger = false,
  onResult,
}: ConfirmDialogProps) {
  return (
    <Modal
      open={open}
      onClose={() => onResult(false)}
      title={title ?? sq.common.confirmTitle}
      id="confirmModal"
      footer={
        <>
          <Button onClick={() => onResult(false)}>{cancelLabel ?? sq.common.no}</Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={() => onResult(true)}>
            {okLabel ?? sq.common.yes}
          </Button>
        </>
      }
    >
      <p style={{ margin: 0, lineHeight: 1.5 }}>{message}</p>
    </Modal>
  );
}

interface PendingConfirm {
  message: string;
  options: ConfirmOptions;
  resolve: (value: boolean) => void;
}

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const confirm = useCallback<ConfirmFn>(
    (message, options = {}) =>
      new Promise<boolean>((resolve) => {
        setPending({ message, options, resolve });
      }),
    [],
  );

  const finish = useCallback((result: boolean) => {
    setPending((current) => {
      current?.resolve(result);
      return null;
    });
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialog
        open={pending !== null}
        message={pending?.message ?? ''}
        title={pending?.options.title}
        okLabel={pending?.options.okLabel}
        cancelLabel={pending?.options.cancelLabel}
        danger={pending?.options.danger ?? false}
        onResult={finish}
      />
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const confirm = useContext(ConfirmContext);
  if (!confirm) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return confirm;
}
